import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { CiSearch } from "react-icons/ci";
import { BsFillXCircleFill } from "react-icons/bs";
import { FiFilter, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import moment from "moment";
import "../../styles/adminUsers.css";
import "../../styles/TransactionHistory.css";
import useActiveGames from "../../hooks/useActiveGames";
import "../../styles/ReportManage.css";
import Select from "react-select";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import TransactionDetailsModal from "./modals/TransactionDetailsModal";
import { AuthContext } from "../../context/AuthContext";
import BalanceLog from "./components/BalanceLog";
import BalanceLogDownload from "./components/BalanceLogDownload";
import BalanceHistory from "./components/BalanceHistory";
import BalanceHistoryDownload from "./components/BalanceHistoryDownload";
import BalanceProfitLoss from "./components/BalanceProfitLoss";
import TransactionHistoryDownload from "./components/TransactionHistoryDownload";
import TransactionHistoryComponent from "./components/TransactionHistory";
import { selectCustomStyles } from "../../utils/utils";
import MobileTransactionHistory from "./components/MobileTransactionHistory";

const Tabs = [
  { label: "Balance Log", value: "balance_log" },
  { label: "Balance History", value: "balance_history" },
  { label: "Balance Profit/Loss", value: "balance_profit_loss" },
  { label: "Transaction History", value: "transaction_history" },
];

const TransactionHistory = () => {
  // Session options follow the running games; the two fixed entries here
  // meant a game added later could never be filtered for.
  const { games: activeGames } = useActiveGames({ remember: false });

  const { user } = useContext(AuthContext);
  const txApi = new TransactionHistoryServices();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [activeTab, setActiveTab] = useState((user.role_id === 3 || user.role_id === 4) ? "transaction_history" : "balance_log");
  const [userId, setUserId] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [subAdminUsers, setSubAdminUsers] = useState([]);
  const [outletUsers, setOutletUsers] = useState([]);
  const [mobileUsers, setMobileUsers] = useState([]);

  const [q, setQ] = useState("");
  const [from, setFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [paymentType, setPaymentType] = useState("");
  const [gameType, setGameType] = useState("");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);

  /**
   * Mobile-only bottom-sheet filter UI. Desktop CSS hides every mobile-only
   * element (toolbar, backdrop, sheet) so the inline desktop header is
   * preserved exactly as before. The sheet itself contains its own copies of
   * the filter inputs that bind to the same state, so the data flow stays
   * single-source-of-truth.
   */
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const defaultFrom = moment().startOf("month").format("YYYY-MM-DD");
  const defaultTo = moment().format("YYYY-MM-DD");
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (userId) n += 1;
    if (gameType) n += 1;
    if (paymentType) n += 1;
    if (from && from !== defaultFrom) n += 1;
    if (to && to !== defaultTo) n += 1;
    return n;
  }, [userId, gameType, paymentType, from, to, defaultFrom, defaultTo]);

  const clearAllFilters = useCallback(() => {
    setUserId("");
    setQ("");
    setGameType("");
    setPaymentType("");
    setFrom(defaultFrom);
    setTo(defaultTo);
    setPage(1);
  }, [defaultFrom, defaultTo]);

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileFiltersOpen]);

  const loadBalanceLog = async () => {
    try {
      const res = await txApi.getBalanceLogHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };
  const loadBalanceHistory = async () => {
    try {
      const res = await txApi.getBalanceHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceProfileLoss = async () => {
    try {
      const res = await txApi.getBalanceProfileLoss({
        page,
        limit,
        q,
        from,
        to,
        userId,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    try {
      const res =
        user.role_id === 4
          ? await txApi.getMobileTransactionHistory({
              page,
              limit,
              q,
              from,
              to,
              userId,
              gameType,
            })
          : await txApi.getTransactionHistory({
              page,
              limit,
              q,
              from,
              to,
              userId,
              gameType,
            });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    const res = await txApi.getAdminUsers();
    if (res.isSuccess) {
      setAdminUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load admin users");
    }
  };
  const loadSubAdminUsers = async () => {
    const res = await txApi.getSubAdminUsers();
    if (res.isSuccess) {
      setSubAdminUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load sub admins");
    }
  };
  const loadOutletUsers = async () => {
    const res = await txApi.getOutletUsers();
    if (res.isSuccess) {
      setOutletUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load outlet users");
    }
  };

  const loadMobileUsers = async () => {
    const res = await txApi.getMobileUsers();
    if (res.isSuccess) {
      setMobileUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load mobile users");
    }
  };

  useEffect(() => {
    if (user.role_id === 1) {
      loadAdminUsers();
    }
    if (user.role_id === 1 || user.role_id === 2) {
      loadSubAdminUsers();
    }
    if ((user.role_id === 2 || user.role_id === 5) || user.role_id === 1) {
      loadOutletUsers();
    }
    if ((user.role_id === 2 || user.role_id === 5) || user.role_id === 1 || user.role_id === 3) {
      loadMobileUsers();
    }
  }, []);

  const loadTx = useCallback(async () => {
    setLoading(true);
    if (activeTab === "balance_log") {
      await loadBalanceLog();
    } else if (activeTab === "balance_history") {
      await loadBalanceHistory();
    } else if (activeTab === "balance_profit_loss") {
      await loadBalanceProfileLoss();
    } else if (activeTab === "transaction_history") {
      await loadTransactionHistory();
    }
  }, [activeTab, page, limit, q, from, to, userId, gameType, paymentType]);

  useEffect(() => {
    loadTx();
  }, [loadTx]);

  const openDetails = async (id) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetails(null);
    try {
      const res = await txApi.getTransactionDetails(id);
      if (res.isSuccess) setDetails(res.data);
      else toast.error(res.message || "Failed to load details");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const onSearch = (e) => {
    setPage(1);
    setQ(e.target.value);
  };

  const onFrom = (e) => {
    setPage(1);
    setFrom(e.target.value);
  };

  const onTo = (e) => {
    setPage(1);
    setTo(e.target.value);
  };

  return (
    <div>
      <div
        className={`tx-header${mobileFiltersOpen ? " tx-filters-open" : ""}`}
      >
        <div className="tx-header-filters-section">
          <div className="transaction-tab-wrapper">
            {Tabs.map((tab) => {
              if (tab.value === "balance_profit_loss" && user.role_id !== 1) {
                return null;
              }
              return (
                <button
                  key={tab.value}
                  className={activeTab === tab.value ? "active" : ""}
                  onClick={() => {
                    setUserId("");
                    setQ("");
                    setGameType("");
                    setPaymentType("");
                    setActiveTab(tab.value);
                    setPage(1);
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="d-flex align-items-center gap-2">
            {activeTab === "balance_log" && (
              <BalanceLogDownload
                from={from}
                to={to}
                userId={userId}
                q={q}
                paymentType={paymentType}
                userName={
                  [...adminUsers, ...subAdminUsers, ...outletUsers, ...mobileUsers].find(
                    (u) => Number(u.id) === Number(userId),
                  )?.name ||
                  (user?.name ?? "All")
                }
              />
            )}
            {activeTab === "balance_history" && (
              <BalanceHistoryDownload
                from={from}
                to={to}
                userId={userId}
                q={q}
                paymentType={paymentType}
                userName={
                  [...adminUsers, ...subAdminUsers, ...outletUsers, ...mobileUsers].find(
                    (u) => Number(u.id) === Number(userId),
                  )?.name ||
                  (user?.name ?? "All")
                }
              />
            )}
            {activeTab === "transaction_history" && (
              <>
                <TransactionHistoryDownload
                  from={from}
                  to={to}
                  userId={userId}
                  q={q}
                  gameType={gameType}
                  userName={
                    [...adminUsers, ...subAdminUsers, ...outletUsers, ...mobileUsers].find(
                      (u) => Number(u.id) === Number(userId),
                    )?.name ||
                    (user?.name ?? "All")
                  }
                />
                <select
                  className="form-control"
                  value={gameType}
                  onChange={(e) => {
                    setGameType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Select Session</option>
                  {activeGames.map((g) => (
                    <option key={g.id} value={g.game_code}>
                      {g.game_name}
                    </option>
                  ))}
                </select>
              </>
            )}
            {(activeTab === "balance_log" ||
              activeTab === "balance_history") && (
              <select
                className="form-control"
                value={paymentType}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Select Type</option>
                <option value="CREDIT">Credit</option>
                <option value="DEBIT">Debit</option>
              </select>
            )}
            <button
              type="button"
              className="transaction-clear-button"
              onClick={() => {
                setUserId("");
                setQ("");
                setGameType("");
                setPaymentType("");
                setFrom(moment().startOf("month").format("YYYY-MM-DD"));
                setTo(moment().format("YYYY-MM-DD"));
                setPage(1);
              }}
            >
              {/* <BsFillXCircleFill size={18} className="search-icon" /> */}
              <span>Clear</span>
            </button>
          </div>
        </div>

        <div className="tx-header-row">
          <div className="admin-search tx-search">
            <div className="search-box" style={{ width: "100%" }}>
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search (reference, user, type...)"
                value={q}
                onChange={onSearch}
              />
            </div>
          </div>
          {user.role_id === 1 &&
            activeTab !== "transaction_history" &&
            activeTab !== "balance_profit_loss" && (
              <div>
                <Select
                  styles={selectCustomStyles}
                  aria-labelledby="aria-label"
                  inputId="admin-users-select"
                  instanceId="admin-users-select"
                  name="admin-users-select"
                  isSearchable={true}
                  openMenuOnFocus={true}
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  options={adminUsers.map((u) => ({
                    value: Number(u.id),
                    label: u.username,
                  }))}
                  value={
                    adminUsers
                      .map((u) => ({ value: Number(u.id), label: u.username }))
                      .find((o) => o.value === Number(userId)) || null
                  }
                  onChange={(opt) => {
                    setUserId(opt?.value ?? null);
                    setPage(1);
                  }}
                  placeholder="Select Admin"
                  isClearable={true}
                />
              </div>
            )}
          {(user.role_id === 1 || user.role_id === 2) && (
            <div>
              <Select
                styles={selectCustomStyles}
                inputId="sub-admin-users-select"
                instanceId="sub-admin-users-select"
                name="sub-admin-users-select"
                isSearchable
                openMenuOnFocus
                options={subAdminUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  subAdminUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                }}
                placeholder="Select Sub Admin"
                isClearable
              />
            </div>
          )}
          {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5)) && (
            <div>
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="outlet-users-select"
                instanceId="outlet-users-select"
                name="outlet-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={outletUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  outletUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                }}
                placeholder="Select Outlet"
                isClearable={true}
              />
            </div>
          )}
          {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5) || user.role_id === 3) && (
            <div>
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="mobile-users-select"
                instanceId="mobile-users-select"
                name="mobile-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={mobileUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  mobileUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                }}
                placeholder="Select Mobile"
                isClearable={true}
              />
            </div>
          )}

          <div className="tx-filters">
            <div className="tx-date">
              <label>From</label>
              <input type="date" value={from} onChange={onFrom} />
            </div>
            <div className="tx-date">
              <label>To</label>
              <input type="date" value={to} onChange={onTo} />
            </div>
          </div>
        </div>

        {/* ===== MOBILE ONLY — Toolbar (search + Filter pill) ===== */}
        <div className="tx-mobile-toolbar">
          <div className="tx-mobile-search">
            <div className="search-box">
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search (reference, user, type...)"
                value={q}
                onChange={onSearch}
              />
            </div>
          </div>
          <button
            type="button"
            className="tx-mobile-filter-btn"
            onClick={() => setMobileFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={mobileFiltersOpen}
            aria-label="Open filters"
          >
            <FiFilter size={16} />
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className="tx-mfb-count">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* ===== MOBILE ONLY — Bottom-sheet backdrop ===== */}
        <div
          className="tx-sheet-backdrop"
          onClick={() => setMobileFiltersOpen(false)}
          aria-hidden="true"
        />

        {/* ===== MOBILE ONLY — Bottom sheet ===== */}
        <aside
          className="tx-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Filter transactions"
        >
          <div className="tx-sheet-grab" aria-hidden="true">
            <span />
          </div>
          <div className="tx-sheet-head">
            <div className="tx-sheet-head-left">
              <span className="tx-sheet-icon" aria-hidden="true">
                <FiFilter size={16} />
              </span>
              <div className="tx-sheet-titles">
                <h4 className="tx-sheet-title">
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="tx-sheet-count">{activeFilterCount}</span>
                  )}
                </h4>
                <p className="tx-sheet-subtitle">
                  {activeFilterCount > 0
                    ? `${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""}`
                    : "Refine your transactions"}
                </p>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="tx-sheet-reset"
                onClick={clearAllFilters}
              >
                Reset
              </button>
            )}
            <button
              type="button"
              className="tx-sheet-close"
              onClick={() => setMobileFiltersOpen(false)}
              aria-label="Close filters"
            >
              <FiX size={18} />
            </button>
          </div>

          <div className="tx-sheet-body">
            {/* Session / Type — context-specific quick filter */}
            {activeTab === "transaction_history" && (
              <div className="tx-sheet-section">
                <div className="tx-sheet-section-label">Session</div>
                <select
                  className="tx-sheet-select"
                  value={gameType}
                  onChange={(e) => {
                    setGameType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All sessions</option>
                  {activeGames.map((g) => (
                    <option key={g.id} value={g.game_code}>
                      {g.game_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(activeTab === "balance_log" ||
              activeTab === "balance_history") && (
              <div className="tx-sheet-section">
                <div className="tx-sheet-section-label">Transaction type</div>
                <div className="tx-sheet-chips">
                  {[
                    { v: "", label: "All" },
                    { v: "CREDIT", label: "Credit" },
                    { v: "DEBIT", label: "Debit" },
                  ].map((o) => (
                    <button
                      key={o.v || "all"}
                      type="button"
                      className={`tx-chip${paymentType === o.v ? " active" : ""}`}
                      onClick={() => {
                        setPaymentType(o.v);
                        setPage(1);
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* User selectors — only render the ones permitted for this role/tab */}
            {user.role_id !== 4 && (
              <div className="tx-sheet-section">
                <div className="tx-sheet-section-label">User</div>
                <div className="tx-sheet-user-selects">
                  {user.role_id === 1 &&
                    activeTab !== "transaction_history" &&
                    activeTab !== "balance_profit_loss" && (
                      <Select
                        styles={selectCustomStyles}
                        inputId="m-admin-users-select"
                        instanceId="m-admin-users-select"
                        name="m-admin-users-select"
                        isSearchable
                        openMenuOnFocus
                        options={adminUsers.map((u) => ({
                          value: Number(u.id),
                          label: u.username,
                        }))}
                        value={
                          adminUsers
                            .map((u) => ({
                              value: Number(u.id),
                              label: u.username,
                            }))
                            .find((o) => o.value === Number(userId)) || null
                        }
                        onChange={(opt) => {
                          setUserId(opt?.value ?? null);
                          setPage(1);
                        }}
                        placeholder="Select Admin"
                        isClearable
                      />
                    )}
                  {(user.role_id === 1 || user.role_id === 2) && (
                      <Select
                        styles={selectCustomStyles}
                        inputId="m-sub-admin-users-select"
                        instanceId="m-sub-admin-users-select"
                        name="m-sub-admin-users-select"
                        isSearchable
                        openMenuOnFocus
                        options={subAdminUsers.map((u) => ({
                          value: Number(u.id),
                          label: u.username,
                        }))}
                        value={
                          subAdminUsers
                            .map((u) => ({ value: Number(u.id), label: u.username }))
                            .find((o) => o.value === Number(userId)) || null
                        }
                        onChange={(opt) => {
                          setUserId(opt?.value ?? null);
                          setPage(1);
                        }}
                        placeholder="Select Sub Admin"
                        isClearable
                      />
                  )}
                  {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5)) && (
                    <Select
                      styles={selectCustomStyles}
                      inputId="m-outlet-users-select"
                      instanceId="m-outlet-users-select"
                      name="m-outlet-users-select"
                      isSearchable
                      openMenuOnFocus
                      options={outletUsers.map((u) => ({
                        value: Number(u.id),
                        label: u.username,
                      }))}
                      value={
                        outletUsers
                          .map((u) => ({
                            value: Number(u.id),
                            label: u.username,
                          }))
                          .find((o) => o.value === Number(userId)) || null
                      }
                      onChange={(opt) => {
                        setUserId(opt?.value ?? null);
                        setPage(1);
                      }}
                      placeholder="Select Outlet"
                      isClearable
                    />
                  )}
                  <Select
                    styles={selectCustomStyles}
                    inputId="m-mobile-users-select"
                    instanceId="m-mobile-users-select"
                    name="m-mobile-users-select"
                    isSearchable
                    openMenuOnFocus
                    options={mobileUsers.map((u) => ({
                      value: Number(u.id),
                      label: u.username,
                    }))}
                    value={
                      mobileUsers
                        .map((u) => ({
                          value: Number(u.id),
                          label: u.username,
                        }))
                        .find((o) => o.value === Number(userId)) || null
                    }
                    onChange={(opt) => {
                      setUserId(opt?.value ?? null);
                      setPage(1);
                    }}
                    placeholder="Select Mobile"
                    isClearable
                  />
                </div>
              </div>
            )}

            {/* Date range */}
            <div className="tx-sheet-section">
              <div className="tx-sheet-section-label">Date range</div>
              <div className="tx-sheet-dates">
                <div className="tx-sheet-date">
                  <label htmlFor="m-from">From</label>
                  <input
                    id="m-from"
                    type="date"
                    value={from}
                    onChange={onFrom}
                  />
                </div>
                <div className="tx-sheet-date">
                  <label htmlFor="m-to">To</label>
                  <input id="m-to" type="date" value={to} onChange={onTo} />
                </div>
              </div>
            </div>
          </div>

          <div className="tx-sheet-foot">
            <button
              type="button"
              className="tx-sheet-btn ghost"
              onClick={clearAllFilters}
            >
              <BsFillXCircleFill size={14} />
              <span>Clear all</span>
            </button>
            <button
              type="button"
              className="tx-sheet-btn primary"
              onClick={() => setMobileFiltersOpen(false)}
            >
              Apply
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
          </div>
        </aside>
      </div>

      <div>
        <>
          {activeTab === "balance_log" && (
            <BalanceLog
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "balance_history" && (
            <BalanceHistory
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "balance_profit_loss" && (
            <BalanceProfitLoss
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "transaction_history" && user.role_id !== 4 ? (
            <TransactionHistoryComponent
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          ) : (
            activeTab === "transaction_history" && (
              <MobileTransactionHistory
                userId={userId}
                rows={rows}
                loading={loading}
                page={page}
                pagination={pagination}
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                user={user}
                setDetailsOpen={setDetailsOpen}
                setDetailsLoading={setDetailsLoading}
                setDetails={setDetails}
                openDetails={openDetails}
              />
            )
          )}
        </>
      </div>

      <TransactionDetailsModal
        show={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        loading={detailsLoading}
        data={details}
      />
    </div>
  );
};

export default TransactionHistory;
